import { memo, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import DOMPurify from 'dompurify';
import {
    Text,
    VStack,
    Button,
    HStack,
    TableRow,
    TableCell,
    Table,
    TableBody,
} from '@library';
import CheckmarkIcon from '../../../core/components/icons/CheckmarkIcon';
import Modal from '../../../core/components/Modal';
import useLoginState from '../../../core/hooks/useLoginState';
import type { ProductAttribute } from '../../types';
import normalizeAttributeValue from '../../util/normalizeAttributeValue';
import ProductFalseDataModal from './ProductFalseForm/ProductFalseDataModal';
import styles from './ProductDetailsDescription.module.scss';

export const FalseDataSuccessModal = memo(
    ({
        setSuccessModalOpen,
    }: {
        setSuccessModalOpen: (isOpen: boolean) => void;
    }) => (
        <Modal onClose={() => setSuccessModalOpen(false)}>
            <div className={styles.successModalContent}>
                <CheckmarkIcon circular />
                <div className={styles.message}>
                    <FormattedMessage id="your message was sent" />
                </div>
                <Button onClick={() => setSuccessModalOpen(false)}>
                    <FormattedMessage id="back to product" />
                </Button>
            </div>
        </Modal>
    )
);

const Actions = memo(
    ({
        isLoggedIn,
        setFalseDataModalOpen,
    }: {
        isLoggedIn: boolean;
        setFalseDataModalOpen: (isOpen: boolean) => void;
    }) => {
        return (
            <HStack wrap gap="small">
                {isLoggedIn && (
                    <Button
                        outline
                        onClick={() => {
                            setFalseDataModalOpen(true);
                        }}
                    >
                        <Text>
                            <FormattedMessage id="report incorrect data" />
                        </Text>
                    </Button>
                )}
            </HStack>
        );
    }
);

const Modals = memo(
    ({
        falseDataModalOpen,
        successModalOpen,
        setFalseDataModalOpen,
        setSuccessModalOpen,
        openModal,
    }: {
        falseDataModalOpen: boolean;
        successModalOpen: boolean;
        setSuccessModalOpen: (isOpen: boolean) => void;
        setFalseDataModalOpen: (isOpen: boolean) => void;
        openModal: (modal: string) => void;
    }) => {
        return (
            <>
                <ProductFalseDataModal
                    onClose={() => {
                        setFalseDataModalOpen(false);
                    }}
                    open={falseDataModalOpen}
                    openSuccessModal={() => {
                        openModal('success');
                    }}
                />

                {successModalOpen && (
                    <FalseDataSuccessModal
                        setSuccessModalOpen={setSuccessModalOpen}
                    />
                )}
            </>
        );
    }
);

const ProductSpecificationAttributeItem = memo(
    ({ attribute: { name, value } }: { attribute: ProductAttribute }) => {
        const normalizedHtml = normalizeAttributeValue(value);

        const cellContent = useMemo(
            () =>
                Array.isArray(value) ? (
                    <span
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(value.join(', ')),
                        }}
                    />
                ) : (
                    <span
                        className={styles.attributeHTMLValue}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(normalizedHtml),
                        }}
                    />
                ),
            [normalizedHtml, value]
        );

        return (
            <TableRow>
                <TableCell verticalAlign="top">
                    <div className={styles.label}>
                        <Text medium component="p" secondary>
                            {name}
                        </Text>
                    </div>
                </TableCell>
                <TableCell width="100%" verticalAlign="top">
                    <Text component="p" secondary>
                        {cellContent}
                    </Text>
                </TableCell>
            </TableRow>
        );
    }
);

const ProductDetailsDescription = ({
    description,
    specificationAttributes,
}: {
    description?: string;
    specificationAttributes?: ProductAttribute[];
}) => {
    const isLoggedIn = useLoginState();
    const [falseDataModalOpen, setFalseDataModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    const openModal = (modal: string) => {
        if (modal === 'form') {
            setFalseDataModalOpen(true);
            setSuccessModalOpen(false);
        } else if (modal === 'attribute') {
            setFalseDataModalOpen(false);
            setSuccessModalOpen(false);
        } else {
            setFalseDataModalOpen(false);
            setSuccessModalOpen(true);
        }
    };

    const descriptionComponent = (
        <span
            className={styles.descriptionContent}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: description ?? '' }}
        />
    );

    return (
        <VStack gap="medium">
            {description && (
                <div className={styles.descriptionContainer}>
                    <Text secondary regular>
                        {descriptionComponent}
                    </Text>
                </div>
            )}

            <Actions
                isLoggedIn={isLoggedIn}
                setFalseDataModalOpen={setFalseDataModalOpen}
            />
            <Modals
                falseDataModalOpen={falseDataModalOpen}
                openModal={openModal}
                setFalseDataModalOpen={setFalseDataModalOpen}
                setSuccessModalOpen={setSuccessModalOpen}
                successModalOpen={successModalOpen}
            />
            {specificationAttributes && specificationAttributes.length > 0 && (
                <div className={styles.container}>
                    <Table striped>
                        <thead>
                            <tr>
                                <th className={styles.emptyTableHeaderCell} />
                                <th className={styles.emptyTableHeaderCell} />
                            </tr>
                        </thead>
                        <TableBody>
                            {specificationAttributes.map(attribute => {
                                return (
                                    <ProductSpecificationAttributeItem
                                        attribute={attribute}
                                        key={attribute.name}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}
        </VStack>
    );
};

export default memo(ProductDetailsDescription);
